import { type MaybeRefOrGetter, toValue } from 'vue'

import { pushMeasurement } from '@backmarket/instrument/pushMeasurement'

import type { EntryPoint } from './useShoppingAssistantContext'
import type { HostType } from './useShoppingAssistantHost.injection-key'

export type ShoppingAssistantApiCall =
  | 'start_chat'
  | 'send_message'
  | 'stream_start_chat'
  | 'stream_message'
  | 'push_browsing_context'

export type ShoppingAssistantApiOutcome = 'completed' | 'aborted' | 'errored'

export function useShoppingAssistantObservability(
  hostType: MaybeRefOrGetter<HostType>,
  entryPoint: MaybeRefOrGetter<EntryPoint>,
  entrySource?: MaybeRefOrGetter<string | null>,
) {
  function recordApiCall(params: {
    call: ShoppingAssistantApiCall
    outcome: ShoppingAssistantApiOutcome
    durationMs: number
    ttftMs?: number
    chatId: string | null
    xRequestId: string | null
    httpStatus?: number
    // Snapshot from the caller, taken when the call started rather than when
    // it completes — overrides the composable-level entryPoint, which may
    // have since changed (e.g. the sidebar closed while the call was in flight).
    entryPoint?: EntryPoint
    entrySource?: string | null
  }) {
    const sourceValue = params.entrySource ?? toValue(entrySource ?? null)

    pushMeasurement(
      {
        type: 'shopping_assistant_api',
        values: {
          duration_ms: params.durationMs,
          ...(params.ttftMs !== undefined ? { ttft_ms: params.ttftMs } : {}),
        },
      },
      {
        context: {
          call: params.call,
          outcome: params.outcome,
          host_type: toValue(hostType),
          entry_point: params.entryPoint ?? toValue(entryPoint),
          chat_id: params.chatId ?? '',
          x_request_id: params.xRequestId ?? '',
          ...(sourceValue ? { entry_source: sourceValue } : {}),
          ...(params.httpStatus !== undefined
            ? { http_status: String(params.httpStatus) }
            : {}),
        },
      },
    )
  }

  return { recordApiCall }
}
